import { useState, useEffect } from "react";
import { Modal, Center, Loader, Text } from "@mantine/core";
import { useMutation } from "@apollo/client";
import { gql } from "@/__generated__";
import styles from "./appeal-letter-viewer-modal.module.scss";

const GET_DOWNLOAD_LINK = gql(`
  mutation DownloadAppealLetterLinkForViewer($appealId: ID!) {
    downloadAppealLetterLink(appealId: $appealId)
  }
`);

type AppealLetterViewerModalProps = {
  opened: boolean;
  onClose: () => void;
  appealId: string;
  patientName?: string;
  onError?: (error: string) => void;
};

export function AppealLetterViewerModal({
  opened,
  onClose,
  appealId,
  patientName,
  onError,
}: AppealLetterViewerModalProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [getDownloadLink] = useMutation(GET_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const fetchPdfUrl = async () => {
      if (!opened) {
        return;
      }

      setIsLoading(true);
      setError(null);
      setPdfUrl(null);

      try {
        const resp = await getDownloadLink({
          variables: { appealId },
        });

        if (resp.data?.downloadAppealLetterLink) {
          setPdfUrl(resp.data.downloadAppealLetterLink as string);
        } else {
          throw new Error("Failed to get appeal letter URL");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load appeal letter";
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPdfUrl();
  }, [opened, appealId, getDownloadLink, onError]);

  const handleClose = () => {
    setPdfUrl(null);
    setError(null);
    onClose();
  };

  const title = patientName
    ? `Appeal Letter - ${patientName}`
    : "Appeal Letter";

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="90%"
      title={title}
      classNames={{
        body: styles.modalBody,
        header: styles.modalHeader,
      }}
    >
      {isLoading && (
        <Center className={styles.loadingContainer}>
          <Loader color="dark" />
        </Center>
      )}

      {error && !isLoading && (
        <Center className={styles.errorContainer}>
          <Text c="red">{error}</Text>
        </Center>
      )}

      {pdfUrl && !isLoading && !error && (
        <iframe
          src={pdfUrl}
          className={styles.pdfIframe}
          title="Appeal Letter PDF"
        />
      )}
    </Modal>
  );
}
